import { useEffect, useState } from "react";

/**
 * Shared wall-clock ticker for multiple AgentSteps chips.
 * One setInterval fans out to every subscriber instead of N intervals.
 */

type Listener = (nowMs: number) => void;

const listeners = new Set<Listener>();
let intervalId: ReturnType<typeof setInterval> | null = null;
let sharedNowMs = Date.now();

function ensureTicker(intervalMs: number) {
  if (intervalId != null) return;
  intervalId = setInterval(() => {
    sharedNowMs = Date.now();
    listeners.forEach((listener) => {
      listener(sharedNowMs);
    });
  }, intervalMs);
}

function releaseTicker() {
  if (listeners.size > 0 || intervalId == null) return;
  clearInterval(intervalId);
  intervalId = null;
}

/**
 * Returns the current epoch ms, updating every `intervalMs` while mounted.
 * All concurrent callers share a single interval.
 */
export function useSharedNow(intervalMs = 500): number {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const listener: Listener = (next) => setNowMs(next);
    listeners.add(listener);
    ensureTicker(intervalMs);
    // Sync immediately so a newly mounted chip doesn't wait a full tick.
    setNowMs(Date.now());
    return () => {
      listeners.delete(listener);
      releaseTicker();
    };
  }, [intervalMs]);

  return nowMs;
}

/** Test helper — clears the shared ticker between suites. */
export function __resetSharedNowForTests(): void {
  listeners.clear();
  if (intervalId != null) {
    clearInterval(intervalId);
    intervalId = null;
  }
  sharedNowMs = Date.now();
}
